import { useEffect, useState, useCallback, useMemo } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useForm, FormProvider } from "react-hook-form";
import {
  DateFilterTypesEnum,
  EntityType,
  PaidFlowTypesEnum,
  PaymentDueAccountVM,
  PaymentMethodTypesEnum,
  PaymentMethodVM,
  PaymentService
} from "@propertelligent/client-api";
import apiHelper from "../../utils/apiHelper";
import { NotificationType, PubSub, TopicsEnum } from "../../utils/pubsub";
import {
  BulkPayAmount,
  BulkPayRequest,
  collectSettlePayGroupIds,
  mapPayResponseToAction,
  payPaymentDueAccountGroup,
} from "../../services/paymentDueAccount.service";
import { UserLoginData } from "../../components/common/charts/Enums";
import FixedFooter from "../listings/add-listing-footer/fixed-footer";
import './payment.css';
import PayableCard from "../listings/listing-cards/payables";
import PaymentPayablesORReceivablesList from "./PaymentPayablesORReceivablesList";
import PaymentSummery from "./PaymentSummery";
import Card from "../card";
import SimpleSpinner from "../../components/loader";
import PaymentStatusCard from "./PaymentStatusCard";
import { useCancelNavigation } from "../../utils/useCancelNavigation";
import { buildPaymentDateFilterQueryArgs, filterPayableDues, getPaymentListingPath, loadPayableHeader } from "./paymentListHelpers";
import {
  usePaymentConfirmationPoll,
  dedupeFailedResults,
} from "../../hooks/usePaymentConfirmationPoll";
import { getTransferFailureReasonLabel } from "../../utils/enumHelpers";
import {
  usePaymentSubmitGuard,
  NextIdempotencyKey,
} from "../../hooks/usePaymentSubmitGuard";
import { isPlaidCapablePayeeType } from "./payeePlaidHelpers";

interface ApiData {
  paymentPayeeList: PaymentDueAccountVM[];
  paymentDetail: PaymentDueAccountVM | null;
  paymentMethod: PaymentMethodVM[];
}

interface PaymentSelectionState {
  selectedPayments: PaymentDueAccountVM[];
  totalAmount: number;
  /** Partial overrides for PUT /pay/group; omit when empty. */
  amounts?: BulkPayAmount[];
}

interface PaymentFormData {
  PaymentModeId: string;
  paymentMethodTypeId: string;
  checkNum: string;
  transactionId: string;
  assistanceProvider: string;
  date: string;
}

const INITIAL_API_DATA: ApiData = {
  paymentMethod: [],
  paymentPayeeList: [],
  paymentDetail: null
};

const DATE_FILTERS = {
  NEXT_30_DAYS: DateFilterTypesEnum.Next30Days,
  NEXT_3_MONTHS: DateFilterTypesEnum.Next3Months,
  NEXT_6_MONTHS: DateFilterTypesEnum.Next6Months,
};

// Payment recording modes -- mirrors PaymentMethodTypesEnum on the backend.
const PAYMENT_MODE = PaymentMethodTypesEnum;

const PayablesPostPayment = () => {
  const [data, setData] = useState<ApiData>(INITIAL_API_DATA);
  const [isLoading, setIsLoading] = useState(false);
  const { isSubmitting: isPaySubmitting, runGuarded: runPayGuarded } =
    usePaymentSubmitGuard();
  const [paymentState, setPaymentState] = useState<PaymentSelectionState>({
    selectedPayments: [],
    totalAmount: 0,
  });
  const [fullyQualifiedFilter, setFullyQualifiedFilter] = useState("paidFlowTypeId == 1");
  const [dateFilterType, setDateFilterType] = useState<number | null>(null);
  const [pollIds, setPollIds] = useState<number[]>([]);
  const [pendingFallbackMessage, setPendingFallbackMessage] = useState<string | undefined>();

  const navigate = useNavigate();
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const organizationId = Number(sessionStorage.getItem(UserLoginData.organization_id));

  // Same entity resolution as EditPayable — read from URL search params
  const isEntity = searchParams.get('isEntity') === 'true';
  const entityId = Number(searchParams.get('entityId')) || undefined;
  const entityTypeId = Number(searchParams.get('entityTypeId')) || undefined;
  const payeeAccountId = searchParams.get('pai');

  const listingPath = useMemo(
    () =>
      getPaymentListingPath({
        isEntity,
        entityId,
        entityTypeId,
        paidFlowTypeId: PaidFlowTypesEnum.Payable,
      }),
    [isEntity, entityId, entityTypeId],
  );

  const fetchPayableHeader = useCallback(
    () => loadPayableHeader(isEntity, entityTypeId, entityId, payeeAccountId, id),
    [id, isEntity, entityTypeId, entityId, payeeAccountId],
  );

  // Does not catch its own errors: refreshAfterDelete needs to tell "fetch failed" apart from
  // "fetched fine, there's genuinely nothing left" (a caught-and-swallowed null would collapse
  // both into the same outcome and wrongly navigate the user away on a transient failure).
  const fetchInitialData = useCallback(async (): Promise<PaymentDueAccountVM | null> => {
    setIsLoading(true);
    try {
      const [paymentMethod, detail] = await Promise.all([
        apiHelper(
          PaymentService.getApiPaymentMethodOrganizationlist,
          { showNotification: false },
          organizationId
        ),
        fetchPayableHeader(),
      ]);

      setData(prev => ({
        ...prev,
        paymentMethod: paymentMethod?.data?.success || [],
        paymentDetail: detail,
      }));
      return detail;
    } finally {
      setIsLoading(false);
    }
  }, [organizationId, fetchPayableHeader]);


  const fetchPaymentPayeeList = useCallback(async () => {
    try {
      let res;
      const dateFilterArgs = buildPaymentDateFilterQueryArgs(dateFilterType);

      if (isEntity && entityTypeId === EntityType.Property && payeeAccountId && entityId) {
        res = await apiHelper(
          PaymentService.getApiInvoicePayeeAccountPropertyList,
          { showNotification: false },
          Number(payeeAccountId),
          Number(entityId),
          undefined,
          undefined,
          undefined,
          undefined,
          undefined,
          undefined,
          undefined,
          ...dateFilterArgs,
        );
      } else if (isEntity && entityTypeId === EntityType.Unit && payeeAccountId && entityId) {
        res = await apiHelper(
          PaymentService.getApiInvoicePayeeAccountUnitList,
          { showNotification: false },
          Number(payeeAccountId),
          Number(entityId),
          undefined,
          undefined,
          undefined,
          undefined,
          undefined,
          undefined,
          undefined,
          ...dateFilterArgs,
        );
      } else if (isEntity && entityTypeId === EntityType.Tenant && entityId) {
        res = await apiHelper(
          PaymentService.getApiInvoicePayeeAccountTenantList,
          { showNotification: false },
          Number(entityId),
          undefined,
          undefined,
          undefined,
          undefined,
          undefined,
          undefined,
          undefined,
          ...dateFilterArgs,
        );
      } else {
        res = await apiHelper(
          PaymentService.getApiPaymentDueAccountInvoicelist,
          { showNotification: false },
          id,
          undefined,
          undefined,
          undefined,
          undefined,
          undefined,
          undefined,
          undefined,
          ...dateFilterArgs,
        );
      }

      setData(prev => ({
        ...prev,
        paymentPayeeList: filterPayableDues<PaymentDueAccountVM>(res?.data?.success || [])
      }));

      return res;
    } catch (error) {
      // Does not rethrow: this already shows its own accurate toast, and refreshAfterDelete's
      // Promise.all only branches on fetchInitialData's result -- rethrowing here would also
      // trip refreshAfterDelete's catch, double-toasting a single failure.
      PubSub.publish(TopicsEnum.Notification.toString(), {
        message: "Failed to load payment payee list",
        type: NotificationType.Failure,
      });
    }
  }, [isEntity, entityTypeId, payeeAccountId, entityId, dateFilterType, fullyQualifiedFilter, id]);


  useEffect(() => {
    fetchInitialData().catch(() => {
      PubSub.publish(TopicsEnum.Notification.toString(), {
        message: "Failed to load required data",
        type: NotificationType.Failure,
      });
    });
  }, [fetchInitialData]);

  useEffect(() => {
    fetchPaymentPayeeList();
  }, [fetchPaymentPayeeList]);

  const refreshAfterDelete = useCallback(async () => {
    setPaymentState({ selectedPayments: [], totalAmount: 0, amounts: undefined });
    try {
      const [detail] = await Promise.all([fetchInitialData(), fetchPaymentPayeeList()]);
      if (!detail) {
        navigate(listingPath, { replace: true });
      }
    } catch {
      PubSub.publish(TopicsEnum.Notification.toString(), {
        message: "Failed to refresh payments after delete",
        type: NotificationType.Failure,
      });
    }
  }, [fetchInitialData, fetchPaymentPayeeList, navigate, listingPath]);

  const handleDeleteSuccess = useCallback(() => {
    navigate(listingPath, { replace: true });
  }, [navigate, listingPath]);

  const handleSetSelectedPayments = useCallback((
    payments: PaymentDueAccountVM[],
    totalAmount: number,
    amounts?: BulkPayAmount[],
  ) => {
    setPaymentState({
      selectedPayments: payments,
      totalAmount,
      amounts,
    });
  }, []);

  const methods = useForm<PaymentFormData>({
    mode: "onChange",
    defaultValues: {
      PaymentModeId: "",
      paymentMethodTypeId: "",
      checkNum: "",
      transactionId: "",
      assistanceProvider: "",
      date: new Date().toISOString().split('T')[0]
    }
  });

  const onNavigate = useCancelNavigation(listingPath);

  const selectedPaymentMethodTypeId = methods.watch("paymentMethodTypeId");
  const submitButtonLabel = Number(selectedPaymentMethodTypeId) === PAYMENT_MODE.Plaid ? "Pay" : "Save";

  // Hide Plaid when the payable's payee type cannot receive an online transfer (Bank Account,
  // Vendor, Utility, …). Prefer the header detail; fall back to the first selected due.
  const showPlaid = useMemo(
    () =>
      isPlaidCapablePayeeType(
        data.paymentDetail?.payeeAccountTypeId ??
          paymentState.selectedPayments[0]?.payeeAccountTypeId,
      ),
    [data.paymentDetail?.payeeAccountTypeId, paymentState.selectedPayments],
  );

  // If Plaid was already selected and the payee type is not Plaid-capable, fall back to Cash
  // so we never submit PaymentMethodTypeId=Plaid for BankAccount/Vendor/etc.
  useEffect(() => {
    if (showPlaid) return;
    if (Number(selectedPaymentMethodTypeId) !== PAYMENT_MODE.Plaid) return;
    methods.setValue("paymentMethodTypeId", String(PAYMENT_MODE.Cash), {
      shouldDirty: true,
      shouldValidate: true,
    });
    methods.setValue("PaymentModeId", "", { shouldValidate: true });
  }, [showPlaid, selectedPaymentMethodTypeId, methods]);

  const { status: pollStatus, results: pollResults } = usePaymentConfirmationPoll(pollIds);

  // Resolved (success or failure) stops here and stays on-screen so the dismissible outcome
  // card below can render -- navigation/the confirmation toast now happen from the user's own
  // dismiss action instead of automatically. Only timeout still falls through on its own, since
  // there's no per-id result to show the user in that case.
  useEffect(() => {
    if (pollStatus === "timeout") {
      PubSub.publish(TopicsEnum.Notification.toString(), {
        message:
          pendingFallbackMessage ??
          "Payment submitted -- check back later for confirmation",
        type: NotificationType.Warning,
      });
      setPollIds([]);
      methods.reset();
      onNavigate();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- methods/onNavigate/pendingFallbackMessage
    // are stable/read-latest-via-closure for this one-shot resolution effect; re-running it on their
    // identity churn would risk double-firing the toast+navigate.
  }, [pollStatus]);

  const failedPollResults = useMemo(
    () =>
      dedupeFailedResults(
        Object.entries(pollResults)
          .filter(([, result]) => result.failed)
          .map(([id, result]) => ({
            id: Number(id),
            reasonLabel: getTransferFailureReasonLabel(result.failureReason),
          })),
      ),
    [pollResults],
  );

  const successPollResults = useMemo(
    () =>
      Object.keys(pollResults)
        .filter((id) => !pollResults[Number(id)].failed)
        .map((id) => ({ id: Number(id) })),
    [pollResults],
  );

  // Dismissing the success card is the user's acknowledgement -- that's when the confirmation
  // toast fires and the screen navigates away, not the moment the poll itself resolves.
  const handleDismissSuccess = useCallback(() => {
    PubSub.publish(TopicsEnum.Notification.toString(), {
      message: "Payment confirmed",
      type: NotificationType.Success,
    });
    setPollIds([]);
    methods.reset();
    onNavigate();
  }, [methods, onNavigate]);

  // Dismissing the failure card returns the user to the form so they can retry the ones that
  // didn't go through. A grouped submission can be a MIX of outcomes -- drop any id that already
  // succeeded from the still-selected set (and its now-stale partial-amount override) so a retry
  // can't resend an already-paid line, and refetch the dues list so the row list reflects the
  // real, current server state instead of what was on screen before Pay was clicked.
  const handleDismissFailure = useCallback(() => {
    const succeededIds = new Set(successPollResults.map((result) => result.id));
    setPaymentState((prev) => {
      const remaining = prev.selectedPayments.filter((p) => !succeededIds.has(p.id));
      return {
        selectedPayments: remaining,
        totalAmount: remaining.reduce((sum, p) => sum + (p.amount || 0), 0),
        amounts: prev.amounts?.filter((a) => !succeededIds.has(a.paymentDueAccountId)),
      };
    });
    setPollIds([]);
    fetchPaymentPayeeList();
  }, [successPollResults, fetchPaymentPayeeList]);

  const submitPayments = async (
    formData: PaymentFormData,
    nextKey: NextIdempotencyKey,
  ) => {



    if (paymentState.selectedPayments.length === 0) {
      PubSub.publish(TopicsEnum.Notification.toString(), {
        message: "Please select at least one payment",
        type: NotificationType.Warning,
      });
      return;
    }



    const paymentMethodTypeId = Number(formData?.paymentMethodTypeId);

    // Full rows expand every grouped id; Partial rows send only ids that received amounts.
    const paymentDueAccountIds = collectSettlePayGroupIds(
      paymentState.selectedPayments,
      {},
      paymentState.amounts,
    );

    if (paymentDueAccountIds.length === 0) {
      PubSub.publish(TopicsEnum.Notification.toString(), {
        message: "Please select at least one payment",
        type: NotificationType.Warning,
      });
      return;
    }

    const payload: BulkPayRequest = {
      paymentDueAccountIds,
      paymentMethodTypeId,
      paymentDate: new Date(formData.date).toISOString(),
      idempotencyKey: nextKey(),
    };

    if (paymentState.amounts?.length) {
      payload.amounts = paymentState.amounts;
    }

    // Only send the field the selected recording mode requires.
    if (paymentMethodTypeId === PAYMENT_MODE.Plaid) {
      payload.fromPaymentMethodId = Number(formData?.PaymentModeId);
    } else if (paymentMethodTypeId === PAYMENT_MODE.Check) {
      payload.checkNumber = formData?.checkNum;
    } else if (paymentMethodTypeId === PAYMENT_MODE.Other) {
      payload.transactionId = formData?.transactionId;
    }

    const response = await apiHelper(
      payPaymentDueAccountGroup,
      { showNotification: false },
      payload,
    );

    const action = mapPayResponseToAction(response);
    const pendingCount = response?.data?.success?.pendingCount ?? 0;

    // Plaid/Default reserved at least one due for transfer -- hold this screen in a loading
    // state and poll for the real outcome instead of immediately toasting+navigating away.
    if (action.status === "success" && pendingCount > 0) {
      setPendingFallbackMessage(action.message);
      setPollIds(paymentDueAccountIds);
      return;
    }

    PubSub.publish(TopicsEnum.Notification.toString(), {
      message:
        action.message ??
        (action.status === "success"
          ? "Payments processed"
          : "Unable to process payments"),
      type:
        action.notificationType === "success"
          ? NotificationType.Success
          : action.notificationType === "warning"
          ? NotificationType.Warning
          : NotificationType.Failure,
    });

    if (action.status === "success" && action.shouldRefresh) {
      methods.reset();
      onNavigate();
    }
  };

  const onSubmit = (formData: PaymentFormData) =>
    runPayGuarded((nextKey) => submitPayments(formData, nextKey));

  if (isLoading) {
    return (
      <SimpleSpinner myClassName={"Icon50"} isFullPage={true} />
    );
  }

  const showFailureOutcome = pollStatus === "resolved" && failedPollResults.length > 0;
  const showSuccessOutcome = pollStatus === "resolved" && failedPollResults.length === 0 && successPollResults.length > 0;
  const showPollingLoader = pollIds.length > 0 && !showFailureOutcome && !showSuccessOutcome;

  return (
    <div className="payment-container d-flex flex-column gap-1 full-page-content">
      <PayableCard
        id="PayableDetailCard"
        className="payable-detail-header"
        isFooter={true}
        onRefresh={refreshAfterDelete}
        onDeleteSuccess={handleDeleteSuccess}
        dataHookState={{
          rows: data.paymentDetail ? [data.paymentDetail] : [],
          loading: isLoading,
        }}
        isEntity={isEntity}
          entityId={entityId}
          entityTypeId={entityTypeId}
      />
      {
        data.paymentDetail && (
          <>
            <Card className="no-bg p-0">
              <div className="row align-items-center mx-3 my-1">
                <div className="col-md-6">
                  <PaymentPayablesORReceivablesList
                    paymentMode={"Payables"}
                    payments={data.paymentPayeeList}
                    selectedPayments={paymentState.selectedPayments}
                    setSelectedPayments={handleSetSelectedPayments}
                    setDateFilterType={setDateFilterType}
                    setFullyQualifiedFilter={setFullyQualifiedFilter}
                    DATE_FILTERS={DATE_FILTERS}
                  />
                </div>
                <div className="col-md-6">
                  <div className="d-flex flex-column gap-3">
                    <FormProvider {...methods}>
                      <PaymentSummery
                        totalAmount={String(paymentState.totalAmount)}
                        paymentMethods={data.paymentMethod}
                        Payments={paymentState.selectedPayments}
                        showPlaid={showPlaid}
                      />
                    </FormProvider>
                  </div>
                </div>
              </div>
            </Card>

            {(showPollingLoader || showFailureOutcome || showSuccessOutcome) && (
              <div className="payment-status-overlay">
                {showFailureOutcome ? (
                  <PaymentStatusCard
                    status="failure"
                    items={failedPollResults}
                    onDismiss={handleDismissFailure}
                  />
                ) : showSuccessOutcome ? (
                  <PaymentStatusCard
                    status="success"
                    items={successPollResults}
                    onDismiss={handleDismissSuccess}
                  />
                ) : (
                  <PaymentStatusCard status="pending" />
                )}
              </div>
            )}

            <FixedFooter
              onSubmit={methods.handleSubmit(onSubmit)}
              loading={methods.formState.isSubmitting || isPaySubmitting}
              buttonLabel={submitButtonLabel}
              isSubmitDisabled={
                showPollingLoader ||
                showFailureOutcome ||
                showSuccessOutcome ||
                methods.formState.isSubmitting ||
                isPaySubmitting ||
                !methods.formState.isDirty ||
                !methods.formState.isValid
              }
              onCancel={onNavigate}
            />
          </>
        )
      }
    </div>
  );
};

export default PayablesPostPayment;